<template>
  <div class="sales_return__report">
    <ReportFormat :records="reports_data" @show_result="show_result" @download_excel_report="exportExcel" :isLoading="loading">
      <template #report-form>
        <div class="vx-row pt-5 px-5" id="report_sales_return">
          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>From Date</h6>
              </div>
              <div class="vx-col w-3/5 relative">
                <date-picker :appendToBody="false" v-model="fromDate" placeholder="MM-DD-YYYY" format="MM-DD-YYYY" valueType="format" class="w-full" name="Report From Date" />
              </div>
            </div>

            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Customer Name</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  class="w-full"
                  autocomplete
                  :value="customer"
                  @input="(elem) => (customer = elem && elem.value)"
                  placeholder="Please Select Customer"
                  @scrollEnd="getSalesCustomerList(customer_filters.page + 1)"
                  label="business_name"
                  :loading="customer_filters.loading"
                  :extra="[{ label: 'All', value: null }]"
                  :options="customer_list_options"
                />
              </div>
            </div>
          </div>

          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>To Date</h6>
              </div>
              <div class="vx-col w-3/5 relative">
                <date-picker :appendToBody="false" v-model="toDate" placeholder="MM-DD-YYYY" format="MM-DD-YYYY" valueType="format" class="w-full" name="Report To Date" />
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #record-table>
        <template>
          <vs-th>#</vs-th>
          <vs-th>Invoice Number</vs-th>
          <vs-th>Original Invoice #</vs-th>
          <vs-th>Sales Person</vs-th>
          <vs-th>Return Date</vs-th>
          <vs-th>Client Name</vs-th>
          <vs-th>Omma license #</vs-th>
          <vs-th>Role Type</vs-th>
          <vs-th>Category</vs-th>
          <vs-th>Batch #</vs-th>
          <vs-th>Item Name</vs-th>
          <vs-th>Weight Type</vs-th>
          <vs-th>Weight Per Package</vs-th>
          <vs-th>Quantity</vs-th>
          <vs-th>Subtotal</vs-th>
          <vs-th>Discount Amount</vs-th>
          <vs-th>Total Tax</vs-th>
          <vs-th>Total</vs-th>
        </template>

        <template ref="tableBody">
          <vs-tr :data="tr" :key="i" v-for="(tr, i) in sales_return_data">
            <vs-td>{{ i + 1 }}</vs-td>
            <vs-td>{{ tr.salesReturn.return_code }}</vs-td>
            <vs-td>{{ tr.sales.sales_code }}</vs-td>
            <vs-td>{{ tr.user.business_name }}</vs-td>
            <vs-td>{{ formatDate(tr.salesReturn.return_date) }}</vs-td>
            <vs-td>{{ tr.sales.customer.business_name }}</vs-td>
            <vs-td>{{ tr.sales.customer.license_number }}</vs-td>
            <vs-td>{{ tr.sales.customer.type }}</vs-td>
            <vs-td>{{ tr.product.category.category_name }}</vs-td>
            <vs-td>{{ tr.product.batch && tr.product.batch.batch_number }}</vs-td>
            <vs-td>{{ tr.product.product_name }}</vs-td>
            <vs-td>{{ tr.product.weight_type }}</vs-td>
            <vs-td>{{ tr.product.weight_per_package | weightFilter }}</vs-td>
            <vs-td>{{ tr.return_qty | weightFilter }}</vs-td>
            <vs-td>$ {{ tr.tot_per_unit | currencyFilter }}</vs-td>
            <vs-td>$ {{ tr.discount_amount | currencyFilter }}</vs-td>
            <vs-td>$ {{ tr.tot_taxes_amt | currencyFilter }}</vs-td>
            <vs-td>$ {{ tr.total_cost | currencyFilter }}</vs-td>
          </vs-tr>

          <vs-tr v-if="sales_return_data.length">
            <vs-td colspan="14" class="text-right font-bold">Total :</vs-td>
            <vs-td class="font-bold">$ {{ calculate_return_subTotal | currencyFilter }}</vs-td>
            <vs-td class="font-bold">$ {{ calculate_return_discount | currencyFilter }}</vs-td>
            <vs-td class="font-bold">$ {{ calculate_return_tax | currencyFilter }}</vs-td>
            <vs-td class="font-bold">$ {{ calculate_return_total | currencyFilter }}</vs-td>
          </vs-tr>
        </template>
      </template>
    </ReportFormat>
  </div>
</template>

<script>
import XLSX from 'xlsx'
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import { dateMixins } from '@/mixins/dateMixins.js'
import { paymentMixins } from '@/utils/paymentTypes'
import { mapState, mapMutations } from 'vuex'
import { RETAILER_SUB_USER } from '@/utils/roles'
import ReportFormat from '@/views/reports/components/ReportFormat.vue'
import Select2 from '@/components/custom/form-elements/Select2.vue'
import { weightMixins } from '@/utils/weightType'

export default {
  name: 'SalesReturnReport',

  components: {
    ReportFormat,
    DatePicker,
    Select2
  },

  data() {
    return {
      page: 1,
      limit: 0,
      search: '',
      date: 'MM-dd-yyyy',
      fromDate: moment().format('MM-DD-YYYY'),
      customer: null,
      toDate: moment().format('MM-DD-YYYY'),
      loading: false,

      customer_filters: {
        page: 1,
        limit: 10,
        loading: false,
        filteredCount: 0
      },
      customer_list_options: []
    }
  },

  mixins: [paymentMixins, dateMixins, weightMixins],

  async mounted() {
    await this.getSalesCustomerList()
  },

  computed: {
    ...mapState('report', ['reports_data', 'total', 'FilteredCount', 'customer_list']),
    ...mapState('product', ['categories']),
    ...mapState('auth', ['parentUser']),

    sales_return_data() {
      return this.reports_data.reduce((acc, item) => {
        if (item.return_qty > 0) {
          acc.push(item)
        }

        return acc
      }, [])
    },

    calculate_return_subTotal() {
      return this.sales_return_data.reduce((acc, item) => {
        return acc + item.tot_per_unit
      }, 0)
    },

    calculate_return_discount() {
      return this.reports_data.reduce((acc, item) => {
        return acc + item.discount_amount
      }, 0)
    },

    calculate_return_tax() {
      return this.reports_data.reduce((acc, item) => {
        return acc + item.tot_taxes_amt
      }, 0)
    },

    calculate_return_total() {
      return this.reports_data.reduce((acc, item) => {
        return acc + item.total_cost
      }, 0)
    }
  },

  methods: {
    moment,
    ...mapMutations('report', { set_state: 'SET_STATE' }),
    ...mapMutations('patient', { set_patient_state: 'SET_STATE' }),
    ...mapMutations('client', { set_client_state: 'SET_STATE' }),

    async getSalesCustomerList(page = this.customer_filters.page) {
      if (this.customer_list_options.length && this.customer_filters.filteredCount === this.customer_list_options.length) return false

      this.customer_filters.loading = true
      this.customer_filters.page = page

      this.$store
        .dispatch(RETAILER_SUB_USER.includes(this.parentUser.roleId) ? 'patient/getPatients' : 'client/getClients', {
          page,
          limit: this.customer_filters.limit
        })
        .then((res) => {
          this.customer_filters.filteredCount = res.FilteredCount

          if (RETAILER_SUB_USER.includes(this.parentUser.roleId)) {
            this.customer_list_options.push(...res.data.map((x) => ({ ...x, business_name: `${x.first_name} ${x.last_name}` })))
          } else {
            this.customer_list_options.push(...res.data)
          }

          this.customer_filters.loading = false
        })
        .catch((err) => {
          const { message } = err
          this.customer_filters.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    async show_result() {
      this.loading = true
      const payload = {
        page: this.page,
        limit: this.limit,
        search: this.search,
        clientPatientId: this.customer || null,
        fromDate: moment(this.fromDate).format('YYYY-MM-DD'),
        toDate: moment(this.toDate).format('YYYY-MM-DD')
      }

      this.$store
        .dispatch('report/getSalesReturnReports', payload)
        .then(() => {
          this.loading = false
        })
        .catch((err) => {
          const { message } = err
          this.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    async exportExcel() {
      const data = []

      // make csv data
      this.sales_return_data.forEach((element) => {
        const tempObj = {}

        tempObj['Invoice #'] = element.salesReturn.return_code
        tempObj['Original Invoice #'] = element.sales.sales_code
        tempObj['Sales Person'] = element.user.business_name
        tempObj['Return Date'] = dateMixins.methods.formatDate(element.sales_item_return_date)
        tempObj['Client Name'] = element.sales.customer.business_name
        tempObj['Omma license #'] = element.sales.customer.license_number || ''
        tempObj['Role Type'] = element.sales.customer.type
        tempObj.Category = element.product.category.category_name
        tempObj['Batch #'] = element.product.batch && element.product.batch.batch_number
        tempObj['Item Name'] = element.product.product_name
        tempObj['Weight Type'] = element.product.weight_type
        tempObj['Weight Per Package'] = [this.categories.FLOWER, this.categories.SHAKE].includes(element.product.categoryId)
          ? this.mathRound(element.return_qty, 3)
          : this.mathRound(element.product.weight_per_package, 3)
        tempObj.Quantity = [this.categories.FLOWER, this.categories.SHAKE].includes(element.product.categoryId) ? 0 : this.mathRound(element.return_qty, 3)
        tempObj[''] = null
        tempObj.Subtotal = paymentMixins.methods.mathRound(element.tot_per_unit, 2)
        tempObj['Discount Amount'] = paymentMixins.methods.mathRound(element.discount_amount, 2)
        tempObj['Total Tax'] = paymentMixins.methods.mathRound(element.tot_taxes_amt, 2)
        tempObj.Total = paymentMixins.methods.mathRound(element.total_cost, 2)
        data.push(tempObj)
      })

      // total details - row
      const totalObj = {}
      totalObj[''] = 'Total'
      totalObj.Subtotal = this.mathRound(this.calculate_return_subTotal, 2)
      totalObj['Discount Amount'] = this.mathRound(this.calculate_return_discount, 2)
      totalObj['Total Tax'] = this.mathRound(this.calculate_return_tax, 2)
      totalObj.Total = this.mathRound(this.calculate_return_total, 2)

      // csv headers
      const columnHeader = [
        'Invoice #',
        'Original Invoice #',
        'Sales Person',
        'Return Date',
        'Client Name',
        'Omma license #',
        'Role Type',
        'Category',
        'Batch #',
        'Item Name',
        'Weight Type',
        'Weight Per Package',
        'Quantity',
        '',
        'Subtotal',
        'Discount Amount',
        'Total Tax',
        'Total'
      ]

      // add total details in csv data
      data.splice(0, 0, totalObj)

      // generate csv
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(data, { header: columnHeader })

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1')
      wb.bookType = 'csv'
      XLSX.writeFile(wb, 'Sales Return Report.csv')
    }
  },

  watch: {
    loading() {
      if (this.loading) {
        this.$vs.loading({
          container: '#report_sales_return',
          scale: 0.45
        })
      } else {
        this.$vs.loading.close('#report_sales_return > .con-vs-loading')
      }
    },

    // alert
    async reports_data() {
      if (!this.reports_data.length) {
        this.$vs.notify({
          title: 'Warning',
          text: 'No Records Found !',
          iconPack: 'feather',
          icon: 'icon-alert-triangle',
          color: 'warning'
        })
      }
    }
  },
  filters: {
    //weightFilter rounded conversation
    weightFilter(value) {
      return weightMixins.methods.mathRound(value || 0, 3)
    },

    // currencyFilter rounded to 2 point
    currencyFilter(value) {
      return paymentMixins.methods.mathRound(Number(value), 2)
    }
  },
  async beforeDestroy() {
    await this.set_state({ action: 'reports_data', data: [] })
    await this.set_patient_state({ action: 'patients', data: [] })
    await this.set_client_state({ action: 'clients', data: [] })
  }
}
</script>

<style></style>
